import { useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { derivePreviewImageUrl, placePreview } from '../lib/cardPreview.js';

type Props = { imageUrl: string; caption: string; onOpen: () => void };
let activePreview: { owner: symbol; close: () => void } | null = null;
const HOVER_DELAY_MS = 140;
const LEAVE_DELAY_MS = 100;

export function CardThumbnailPreview({ imageUrl, caption, onOpen }: Props) {
  const owner = useRef(Symbol('card-preview'));
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [shown, setShown] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(() => derivePreviewImageUrl(imageUrl));
  const [imageUnavailable, setImageUnavailable] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const captionId = useId();

  function clearTimers() {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }
  function close() {
    clearTimers();
    setShown(false);
    if (activePreview?.owner === owner.current) activePreview = null;
  }
  function open() {
    clearTimers();
    if (activePreview?.owner !== owner.current) activePreview?.close();
    activePreview = { owner: owner.current, close };
    setPreviewUrl(derivePreviewImageUrl(imageUrl));
    setImageUnavailable(false);
    setShown(true);
  }
  function claimAndScheduleOpen() {
    clearTimers();
    if (activePreview?.owner !== owner.current) activePreview?.close();
    activePreview = { owner: owner.current, close };
    openTimer.current = window.setTimeout(open, HOVER_DELAY_MS);
  }
  function scheduleOpen(event: ReactPointerEvent) {
    if (event.pointerType === 'touch') return;
    claimAndScheduleOpen();
  }
  function scheduleClose() {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    openTimer.current = null;
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    if (!shown) return;
    closeTimer.current = window.setTimeout(close, LEAVE_DELAY_MS);
  }
  function keepOpen() {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }
  function updatePosition() {
    const anchor = buttonRef.current?.getBoundingClientRect();
    const popup = popupRef.current;
    if (!anchor || !popup) return;
    setPosition(placePreview(anchor, popup.getBoundingClientRect(), { width: window.innerWidth, height: window.innerHeight }));
  }

  useEffect(() => () => {
    clearTimers();
    if (activePreview?.owner === owner.current) activePreview = null;
  }, []);
  useLayoutEffect(() => {
    close();
    setPreviewUrl(derivePreviewImageUrl(imageUrl));
    setImageUnavailable(false);
  }, [imageUrl, caption]);
  useEffect(() => {
    if (!shown) return;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!buttonRef.current?.contains(target) && !popupRef.current?.contains(target)) close();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [shown]);
  useLayoutEffect(() => {
    if (!shown) return;
    updatePosition();
  }, [shown, previewUrl, imageUnavailable]);

  return <>
    <button
      ref={buttonRef}
      type="button"
      className="card-thumbnail-button"
      aria-label={`Preview ${caption}`}
      aria-describedby={shown ? captionId : undefined}
      onPointerEnter={scheduleOpen}
      onPointerLeave={scheduleClose}
      onFocus={claimAndScheduleOpen}
      onBlur={scheduleClose}
      onClick={() => { clearTimers(); close(); onOpen(); }}
    ><img src={imageUrl} alt="" loading="lazy" /></button>
    {shown && createPortal(
      <div ref={popupRef} className="card-thumbnail-preview" style={position} onPointerEnter={keepOpen} onPointerLeave={scheduleClose}>
        {imageUnavailable
          ? <div className="card-thumbnail-preview-unavailable">Card image unavailable</div>
          : <img
              src={previewUrl}
              alt=""
              onLoad={() => window.requestAnimationFrame(updatePosition)}
              onError={() => {
                if (previewUrl !== imageUrl) setPreviewUrl(imageUrl);
                else setImageUnavailable(true);
              }}
            />}
        <span id={captionId} title={caption}>{caption}</span>
      </div>, document.body,
    )}
  </>;
}
